import { useEffect, useRef } from 'react';

import _ from '@luna/lodash';

type DebouncedCallback = ReturnType<typeof _.debounce<(callback: () => void) => void>>;

/**
 * A custom hook that creates a debounced callback function.
 *
 * This hook returns a memoized debounced function that delays invoking the provided callback
 * until after `timeToDebounce` milliseconds have elapsed since the last time it was invoked.
 *
 * @param {number} timeToDebounce - The number of milliseconds to delay.
 * @returns A debounced function that takes a callback as its argument.
 *
 * @example
 * const debouncedSearch = useDebounceCallback(300);
 * useEffect(() => {
 *   if (shouldSearch(searchTerm)) {
 *     debouncedSearch(functionToDebounce);
 *   }
 * }, [searchTerm, functionToDebounce, debouncedSearch]);
 * @remarks
 * - The returned function is memoized and will not change between re-renders.
 * - This hook is useful for optimizing performance in scenarios where you want to limit
 *   how often a function is called, such as search input handlers or resize event listeners.
 *
 */
export const useDebounceCallback = (timeToDebounce: number): DebouncedCallback => {
    const ref = useRef<DebouncedCallback>(
        _.debounce((callback: () => void) => callback(), timeToDebounce),
    );

    useEffect(() => {
        return () => {
            ref.current?.cancel();
        };
    }, []);

    return ref.current;
};
